import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../../hooks/redux'
import type { IAdvertise } from '../../../../store/slices/advertisesSlice'
import type { Unit } from '../../../../store/slices/unitsSlice'
import type { User } from '../../../../store/slices/userSlice'
import { isActiveAdvertiseForDropPrice } from '../../../../components/adminModule/landlordEvents/helpers/dropPriceHelpers'
import { openChatByUserID } from '../../../../store/actionsCreators/chatActions'
import useUserType from '../../../../hooks/UseUserType'

type WaitingListItem = Record<string, any>

type Props = {
    mode: 'unit' | 'magic'
    currentUnit: Unit | undefined
    currentAdvertise: IAdvertise | undefined
    advertises: IAdvertise[]
    allUsers: User[]
}

const formatDate = (value: unknown): string => {
    if (value == null || value === '') return 'not set'
    const parsed = new Date(String(value))
    if (Number.isNaN(parsed.getTime())) return 'not set'
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Dubai',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed)
}

const formatDateOnly = (value: unknown): string | null => {
    if (value == null || value === '') return null
    const parsed = new Date(String(value))
    if (Number.isNaN(parsed.getTime())) return null
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Dubai',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    }).format(parsed)
}

const renderActiveDateRange = (startValue: unknown, endValue: unknown): string => {
    const start = formatDateOnly(startValue)
    const end = formatDateOnly(endValue)
    if (start && end) return `${start} - ${end}`
    if (start) return start
    if (end) return end
    return 'No schedule'
}

const normalize = (value: unknown): string => String(value ?? '').trim().toLowerCase()

const isReserveQueueItem = (item: WaitingListItem): boolean => {
    const kind = normalize(item?.kind)
    const status = normalize(item?.status)
    return kind === 'reserve' || status === 'reserve' || status === 'reserved'
}

const isNotifyQueueItem = (item: WaitingListItem): boolean => {
    const kind = normalize(item?.kind)
    return kind === 'notify'
}

const resolveUserName = (user: User | undefined, userId: number | null): string => {
    if (!user) return userId != null ? `User #${userId}` : 'Unknown user'
    const firstName = user?.ContactInfo?.FirstName ?? ''
    const familyName = user?.ContactInfo?.FamilyName ?? ''
    const fullName = `${firstName} ${familyName}`.trim()
    return fullName || user?.username || user?.email || `User #${String(user?.id ?? userId ?? 'Unknown')}`
}

const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }): JSX.Element => (
    <div className="d-flex flex-column mb-2">
        <span className="fw-bold fs-5 text-gray-900">{title}</span>
        {subtitle ? <span className="text-muted fs-8">{subtitle}</span> : null}
    </div>
)

const EmptySection = ({ label }: { label: string }): JSX.Element => (
    <div className="text-muted fs-8 py-2 px-3 bg-light rounded border">{label}</div>
)

export default function AdvertiseParticipation({
    mode,
    currentUnit,
    currentAdvertise,
    advertises,
    allUsers,
}: Props): JSX.Element | null {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { userTypeLowercase } = useUserType()

    if (!currentUnit) return null

    const unitId = Number(currentUnit.id)
    const userMap = new Map(allUsers.map((user) => [Number(user.id), user]))
    const unitAdvertises = advertises.filter((advertise) => Number(advertise?.attributes?.Unit?.data?.id) === unitId)
    const activeAdvertisers = unitAdvertises.filter((advertise) => isActiveAdvertiseForDropPrice(advertise))

    // WaitingList is queue metadata only (reserve/notify)
    // It must not be used as source of truth for active advertise targeting.
    const waitingListRaw = Array.isArray(currentUnit?.attributes?.WaitingList) ? currentUnit.attributes.WaitingList : []
    const reserveQueue = waitingListRaw.filter((item) => isReserveQueueItem(item))
    const notifyQueue = waitingListRaw.filter((item) => isNotifyQueueItem(item))

    const reserveUserIds = new Set(
        reserveQueue
            .map((item) => Number(item?.userID ?? item?.userId ?? item?.UserID ?? item?.user?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
    )
    const activeAdvertiserUserIds = new Set(
        activeAdvertisers
            .map((advertise) => Number(advertise?.attributes?.User?.data?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
    )
    const overlapUserIds = Array.from(reserveUserIds).filter((id) => activeAdvertiserUserIds.has(id))
    const currentAdvertiseUserId = Number(currentAdvertise?.attributes?.User?.data?.id)
    const currentAdvertiseInReserve = Number.isFinite(currentAdvertiseUserId) && reserveUserIds.has(currentAdvertiseUserId)

    if (mode === 'magic' && reserveQueue.length === 0 && notifyQueue.length === 0) return null

    return (
        <div className="fv-row mb-2 m-0 p-2 w-100">
            <div className="separator border border-bso border-3 px-2 my-4"></div>

            <SectionHeader
                title={mode === 'unit' ? 'Advertise Participation' : 'Advertise Queue Snapshot'}
                subtitle={
                    mode === 'unit'
                        ? 'Visibility of active advertisers and queue participants for this unit.'
                        : 'Queue overlap check for this advertise and unit.'
                }
            />

            <div className="mb-4">
                <SectionHeader title="1. Active Advertisers" subtitle="Current active marketing participants." />
                {activeAdvertisers.length === 0 ? (
                    <EmptySection label="No active advertisers on this unit." />
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {activeAdvertisers.map((advertise) => {
                            const userId = Number(advertise?.attributes?.User?.data?.id)
                            const user = userMap.get(userId)
                            const activeDateRange = renderActiveDateRange(advertise?.attributes?.StartDate, advertise?.attributes?.EndDate)
                            const hasValidUser = Number.isFinite(userId) && userId > 0

                            return (
                                <div
                                    key={`active_advertiser_${String(advertise.id)}`}
                                    className="border rounded bg-light p-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3"
                                >
                                    <div className="d-flex flex-column gap-1 flex-grow-1">
                                        <div className="fs-5 fw-bold text-gray-900">{resolveUserName(user, hasValidUser ? userId : null)}</div>
                                        <div className="fs-8 text-muted">
                                            Status: {(advertise?.attributes?.Statuses ?? []).join(', ') || '-'}
                                        </div>
                                        <div className="fs-8 text-muted">Active Date Range: {activeDateRange}</div>
                                        <div>
                                            <span className="badge badge-light-success border border-success py-1 px-2 fs-8">Active Advertise</span>
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2" style={{ minWidth: 160 }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-primary w-100"
                                            style={{ height: 34 }}
                                            onClick={() => navigate(`/${userTypeLowercase}/modalView/magic_advertise_modal/${String(unitId)}/${String(advertise.id)}/catalog`)}
                                        >
                                            Open Advertise
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-info w-100"
                                            style={{ height: 34 }}
                                            disabled={!hasValidUser}
                                            onClick={() => {
                                                if (!hasValidUser) return
                                                navigate(`/${userTypeLowercase}/profile/${String(userId)}`)
                                            }}
                                        >
                                            Open Agent
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-dark w-100"
                                            style={{ height: 34 }}
                                            disabled={!hasValidUser}
                                            onClick={() => {
                                                if (!hasValidUser) return
                                                dispatch(openChatByUserID(userId))
                                                    .then(({ id }: { id: number | null }) => {
                                                        if (id != null) {
                                                            navigate(`/${userTypeLowercase}/chats/${String(id)}`)
                                                        }
                                                    })
                                            }}
                                        >
                                            Message
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            <div className="mb-4">
                <SectionHeader title="2. Reserve Queue" subtitle="Queue-only visibility." />
                {reserveQueue.length === 0 ? (
                    <EmptySection label="No reserve queue records." />
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr className="text-muted fs-8">
                                    <th>Agent</th>
                                    <th>Status</th>
                                    <th>reservedAt</th>
                                    <th>startDate</th>
                                    <th>hideDate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reserveQueue.map((item, index) => {
                                    const userId = Number(item?.userID ?? item?.userId ?? item?.UserID ?? item?.user?.id)
                                    const user = userMap.get(userId)
                                    return (
                                        <tr key={`reserve_queue_${String(userId)}_${String(index)}_${String(item?.adId ?? 'none')}`}>
                                            <td className="fw-semibold text-gray-900">{resolveUserName(user, Number.isFinite(userId) ? userId : null)}</td>
                                            <td className="text-muted">{String(item?.status ?? 'Reserve')}</td>
                                            <td className="text-muted">{formatDate(item?.reservedAt ?? item?.createdAt)}</td>
                                            <td className="text-muted">{formatDate(item?.startDate)}</td>
                                            <td className="text-muted">{formatDate(item?.hideDate)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {mode === 'unit' ? (
                <div className="mb-2">
                    <SectionHeader title="3. Notify Queue" subtitle="Subscription-only visibility." />
                    {notifyQueue.length === 0 ? (
                        <EmptySection label="No notify queue records." />
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr className="text-muted fs-8">
                                        <th>Agent</th>
                                        <th>subscribedAt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notifyQueue.map((item, index) => {
                                        const userId = Number(item?.userID ?? item?.userId ?? item?.UserID ?? item?.user?.id)
                                        const user = userMap.get(userId)
                                        return (
                                            <tr key={`notify_queue_${String(userId)}_${String(index)}`}>
                                                <td className="fw-semibold text-gray-900">{resolveUserName(user, Number.isFinite(userId) ? userId : null)}</td>
                                                <td className="text-muted">{formatDate(item?.subscribedAt ?? item?.createdAt)}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <div className="alert alert-warning py-2 px-3 fs-8 mb-0">
                    {overlapUserIds.length > 0 && <div>{`Overlap: ${overlapUserIds.length} reserve user(s) already have active advertises.`}</div>}
                    {currentAdvertiseInReserve && <div>Current advertise owner is also present in reserve queue.</div>}
                    {overlapUserIds.length === 0 && !currentAdvertiseInReserve && (
                        <div>No reserve/active overlap found for current context.</div>
                    )}
                </div>
            )}
        </div>
    )
}
